import React, { useEffect, useRef, useState } from 'react';

const EXPAND_DURATION_MS = 320;

const CollapsibleSection = ({ title, children, open, onClick }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [contentMounted, setContentMounted] = useState(false);
	const [contentExpanded, setContentExpanded] = useState(false);
	const sectionRef = useRef(null);
	const contentRef = useRef(null);
	const unmountTimerRef = useRef(null);
	const scrollTimerRef = useRef(null);
	const shouldScrollOnExpandRef = useRef(false);

	const scrollSectionIntoView = () => {
		const section = sectionRef.current;
		const container = section?.closest('.job-positions-list-wrapper');
		if (!section || !container) return;

		const header = section.querySelector('.collapsible-header') || section;
		const containerRect = container.getBoundingClientRect();
		const headerRect = header.getBoundingClientRect();
		const scrollPadding = 8;
		const nextScrollTop =
			container.scrollTop +
			(headerRect.top - containerRect.top) -
			scrollPadding;

		container.scrollTo({
			top: Math.max(0, nextScrollTop),
			behavior: 'smooth'
		});
	};

	const toggleSection = event => {
		event.stopPropagation();
		setIsOpen(!isOpen);
	};

	const handleHeaderClick = event => {
		if (onClick) onClick(event);
	};

	useEffect(() => {
		if (open !== undefined) {
			setIsOpen(open);
		}
	}, [open]);

	useEffect(() => {
		clearTimeout(unmountTimerRef.current);
		let cancelled = false;
		let outerFrame;
		let innerFrame;

		if (isOpen) {
			setContentMounted(true);
			outerFrame = requestAnimationFrame(() => {
				innerFrame = requestAnimationFrame(() => {
					if (!cancelled) {
						setContentExpanded(true);
					}
				});
			});
		} else {
			setContentExpanded(false);
			unmountTimerRef.current = setTimeout(() => {
				if (!cancelled) {
					setContentMounted(false);
				}
			}, EXPAND_DURATION_MS);
		}

		return () => {
			cancelled = true;
			cancelAnimationFrame(outerFrame);
			cancelAnimationFrame(innerFrame);
			clearTimeout(unmountTimerRef.current);
		};
	}, [isOpen]);

	useEffect(() => {
		clearTimeout(scrollTimerRef.current);
		shouldScrollOnExpandRef.current = isOpen;

		if (!isOpen) return;

		scrollTimerRef.current = setTimeout(() => {
			if (!shouldScrollOnExpandRef.current) return;

			shouldScrollOnExpandRef.current = false;
			scrollSectionIntoView();
		}, EXPAND_DURATION_MS + 50);

		return () => clearTimeout(scrollTimerRef.current);
	}, [isOpen]);

	const handleContentTransitionEnd = event => {
		if (event.target !== contentRef.current) return;
		if (event.propertyName !== 'grid-template-rows') return;

		if (contentExpanded) {
			if (shouldScrollOnExpandRef.current) {
				shouldScrollOnExpandRef.current = false;
				clearTimeout(scrollTimerRef.current);
				scrollSectionIntoView();
			}
			return;
		}

		clearTimeout(unmountTimerRef.current);
		setContentMounted(false);
	};

	return (
		<div
			ref={sectionRef}
			className={`collapsible-section ${isOpen ? 'open' : ''}`}>
			<div className="collapsible-header" onClick={handleHeaderClick}>
				{title}
				<div className="expand-button" onClick={toggleSection}>
					<img src="/ats/icons/chevron-down-icon.svg" alt="" />
				</div>
			</div>
			{contentMounted && (
				<div
					ref={contentRef}
					className={`collapsible-content${
						contentExpanded ? ' is-expanded' : ''
					}`}
					onTransitionEnd={handleContentTransitionEnd}>
					<div className="collapsible-content-inner">{children}</div>
				</div>
			)}
		</div>
	);
};

export default CollapsibleSection;
